/* The Administration console.
 *
 * A second entry point in the same codebase, built to its own bundle and served
 * on its own host. It shares the API client, the design tokens and the UI
 * components with the member app, and shares no routes with it: nothing a
 * church member loads contains church-management code.
 *
 * SIGN-IN IS A GATE, NOT A ROUTE. There is no /signin path here — an unsigned
 * visitor gets the sign-in card whatever URL they typed, and lands on it after
 * signing in. One less way to end up somewhere half-authenticated.
 */
import { useEffect, useState } from 'react'
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import {
  Building2,
  CreditCard,
  Flag,
  LogOut,
  Settings,
  ShoppingBag,
} from 'lucide-react'

import { api, tokens, SESSION_EXPIRED } from '@/api/client'
import { Avatar } from '@/components/ui/avatar'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { ConsoleSignInPage } from '@/features/console/ConsoleSignInPage'
import { ChurchesPage } from '@/features/console/ChurchesPage'
import { ChurchDetailPage } from '@/features/console/ChurchDetailPage'
import { NewChurchPage } from '@/features/console/NewChurchPage'
import { NotBuiltPage } from '@/features/console/NotBuiltPage'
import { RequestsPage } from '@/features/console/RequestsPage'
import type { Me, Notification } from '@/types/api'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // An admin acting on stale data is worse than a moment's wait: approving
      // a request somebody already approved, or editing a church that has since
      // been deleted.
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

export default function ConsoleApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <Gate />
    </QueryClientProvider>
  )
}

function Gate() {
  const [signedIn, setSignedIn] = useState(() => Boolean(tokens.access))

  /* A refresh token that has expired or been revoked ends the session while the
     page is open. Without this the console would sit there rendering failed
     requests instead of asking for a password. */
  useEffect(() => {
    const done = () => setSignedIn(false)
    window.addEventListener(SESSION_EXPIRED, done)
    return () => window.removeEventListener(SESSION_EXPIRED, done)
  }, [])

  if (!signedIn) {
    return <ConsoleSignInPage onSignedIn={() => setSignedIn(true)} />
  }

  return (
    <BrowserRouter>
      <Shell onSignOut={() => setSignedIn(false)} />
    </BrowserRouter>
  )
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const me = useQuery({ queryKey: ['console', 'me'], queryFn: () => api.get<Me>('/auth/me') })

  async function signOut() {
    // Best effort: the local session is cleared either way, so a failed
    // sign-out call must not leave somebody stuck on a screen they wanted to
    // leave.
    try {
      await api.post('/auth/sign-out', { refresh_token: tokens.refresh }, { auth: false })
    } catch {
      /* ignore */
    }
    tokens.clear()
    queryClient.clear()
    onSignOut()
  }

  const isSuperAdmin = me.data?.role === 'super_admin'

  // SUPER ADMIN ONLY, enforced on an existing session as well. The sign-in
  // page refuses everyone else, but a church admin signed in before that rule
  // existed still holds a valid token on this origin — without this they
  // would keep the owner's console until it expired.
  useEffect(() => {
    if (me.data && me.data.role !== 'super_admin') {
      tokens.clear()
      queryClient.clear()
      onSignOut()
    }
  }, [me.data, onSignOut])

  // Nothing renders before the role is known — the check above must see it
  // first, and the routes choose by it.
  if (!me.data || !isSuperAdmin) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas text-ink-2">
        Loading…
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-canvas">
      <aside className="flex w-[68px] shrink-0 flex-col border-r border-line-2 bg-surface transition-[width] lg:w-[260px]">
        <div className="border-b border-line-2 p-s3 lg:p-s4">
          <Link to="/churches" className="block">
            {/* Below lg the sidebar is icons only, so the wordmark would be a
                clipped word rather than a heading. */}
            <div className="hidden lg:block">
              <div className="text-md font-extrabold tracking-tight">Administration</div>
              <div className="text-sm text-ink-2">
                {isSuperAdmin ? 'Super Admin · App owner' : 'Church Admin'}
              </div>
            </div>
            <div className="grid h-8 w-8 place-items-center rounded-pill bg-brand text-sm font-bold text-white lg:hidden">
              A
            </div>
          </Link>
        </div>

        {/* THE CLIENT'S OWN MENU, in their order:
              Groups · Permissions · Requests · Payments · Store Settings · Settings
            (their "Groups" is our Churches — see docs/CLIENT_TERMINOLOGY.md).

            All six appear even though two are built, because the client will
            look for all six and four of them saying what they are waiting on
            is a plan; two items and four silences is a misread brief. */}
        <nav className="flex-1 space-y-s1 p-s3">
          <NavItem to="/churches" icon={<Building2 className="h-4 w-4" />}>
            Churches
          </NavItem>

          {/* Permissions is deliberately NOT here. Roles belong to a church and
              are set by its own admin in the member app; a Super Admin is in no
              church, so this screen could only ever say "no church selected" —
              which is exactly what the old console's did. */}

          {/* Approving churches is the Super Admin's job. A Church Admin would
              only get a 403, so the link is not offered. */}
          {isSuperAdmin ? (
            <NavItem to="/requests" icon={<Flag className="h-4 w-4" />}>
              Requests
            </NavItem>
          ) : null}

          <NavItem to="/payments" icon={<CreditCard className="h-4 w-4" />}>
            Payments
          </NavItem>
          <NavItem to="/store-settings" icon={<ShoppingBag className="h-4 w-4" />}>
            Store Settings
          </NavItem>
          <NavItem to="/settings" icon={<Settings className="h-4 w-4" />}>
            Settings
          </NavItem>
        </nav>

        <div className="border-t border-line-2 p-s3">
          {me.data ? (
            <div className="mb-s2 flex items-center justify-center gap-s3 px-s2 lg:justify-start">
              <Avatar name={fullName(me.data)} size="sm" />
              <span className="hidden min-w-0 truncate font-semibold lg:block">
                {fullName(me.data)}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={signOut}
            title="Sign out"
            className="flex w-full items-center justify-center gap-s3 rounded px-s3 py-s2 text-base font-semibold text-ink-2 hover:bg-hover hover:text-ink lg:justify-start"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            <span className="hidden lg:block">Sign out</span>
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-s3 sm:p-s5">
        {/* A wide cap rather than 1100px. This is a data console on a desktop
            — a table pinned to a narrow column leaves the rest of a 2000px
            window empty while its own columns are cramped. Still capped, so
            rows do not stretch to absurd widths on an ultrawide display. */}
        <div className="mx-auto max-w-[1600px]">
          {/* The bell sits above the page rather than in the sidebar: it is
              about what needs doing now, not about where to go. */}
          <div className="mb-s2 flex justify-end">
            <NotificationBell routeFor={consoleRouteFor} />
          </div>
          <Routes>
            <Route path="/churches" element={<ChurchesPage />} />
            <Route path="/churches/new" element={<NewChurchPage />} />
            <Route path="/churches/:churchId" element={<ChurchDetailPage />} />
            <Route path="/requests" element={<RequestsPage />} />

            <Route
              path="/payments"
              element={
                <NotBuiltPage
                  title="Payments"
                  summary="Subscriptions and payments."
                />
              }
            />
            <Route
              path="/store-settings"
              element={
                <NotBuiltPage
                  title="Store Settings"
                  summary="What the store sells, and on what terms."
                />
              }
            />
            <Route
              path="/settings"
              element={
                <NotBuiltPage
                  title="Settings"
                  summary="Defaults that apply across every church."
                />
              }
            />

            {/* Churches is home, and anything unrecognised lands there rather
                than on a dead end. */}
            <Route path="*" element={<Navigate to="/churches" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  )
}

/** Where a notification leads IN THE CONSOLE.
 *
 *  The bell itself is the member app's — same paging, same mark-all-read, same
 *  badge — and only the destinations differ. Everything an administrator is
 *  told about is a church waiting to be decided on. */
function consoleRouteFor(n: Notification): string | null {
  return n.entity_type === 'church' ? '/requests' : null
}

/** /auth/me returns the two halves separately; every screen wants them joined.
 *  last_name is genuinely optional, so a plain template would render a
 *  trailing space and Avatar would take its initials from it. */
function fullName(me: Me): string {
  return [me.first_name, me.last_name].filter(Boolean).join(' ')
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      title={typeof children === 'string' ? children : undefined}
      className={({ isActive }) =>
        'flex items-center justify-center gap-s3 rounded px-s3 py-s2 text-base font-semibold transition-colors lg:justify-start ' +
        (isActive ? 'bg-brand-softer text-brand' : 'text-ink-2 hover:bg-hover hover:text-ink')
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className="hidden flex-1 lg:block">{children}</span>

    </NavLink>
  )
}
